/**
 * Meal Mems Restaurant Search
 *
 * Handles Google Places Autocomplete on Shopify homepage.
 * When a restaurant is selected:
 * - If we have a product for it → redirects to Shopify product page
 * - If not → redirects to Next.js app for image selection flow
 *
 * Features:
 * - Typewriter animation for placeholder text
 * - Google Places Autocomplete integration
 */

(function() {
  'use strict';

  // Configuration - Update these for your deployment
  var MEALMEMS_CONFIG = {
    // Your Next.js app URL (Vercel deployment)
    apiBaseUrl: 'https://meal-mems-prod.vercel.app',
    // Endpoint to check if a place has an existing product
    checkEndpoint: '/api/place/check',
    // Debug mode - set to true to see console logs
    debug: false
  };

  // Typewriter configuration
  var TYPEWRITER_CONFIG = {
    prefix: 'Search for ',
    phrases: [
      'the spot where you had your first date',
      'the coffee shop where they know your name',
      'the restaurant from that trip to Italy'
    ],
    typeSpeed: 50,      // ms per character when typing
    deleteSpeed: 30,    // ms per character when deleting
    pauseAtEnd: 2000,   // ms to pause after typing complete phrase
    pauseAtStart: 500   // ms to pause before typing next phrase
  };

  // State
  var mealmemsAutocomplete = null;
  var isProcessing = false;

  // Typewriter state
  var typewriterState = {
    currentPhrase: 0,
    currentChar: 0,
    isDeleting: false,
    isPaused: false,
    timeoutId: null
  };

  /**
   * Log helper for debugging
   */
  function log() {
    if (MEALMEMS_CONFIG.debug) {
      console.log.apply(console, ['[MealMems Search]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  /**
   * Typewriter animation for label text (separate from input placeholder)
   */
  function typewrite() {
    if (typewriterState.isPaused) {
      return;
    }

    var label = document.getElementById('mealmems-typewriter-label');
    if (!label) return;

    var phrase = TYPEWRITER_CONFIG.phrases[typewriterState.currentPhrase];
    var prefix = TYPEWRITER_CONFIG.prefix;

    if (typewriterState.isDeleting) {
      // Deleting characters
      typewriterState.currentChar--;
      label.textContent = prefix + phrase.substring(0, typewriterState.currentChar);

      if (typewriterState.currentChar === 0) {
        // Done deleting, move to next phrase
        typewriterState.isDeleting = false;
        typewriterState.currentPhrase = (typewriterState.currentPhrase + 1) % TYPEWRITER_CONFIG.phrases.length;
        typewriterState.timeoutId = setTimeout(typewrite, TYPEWRITER_CONFIG.pauseAtStart);
        return;
      }

      typewriterState.timeoutId = setTimeout(typewrite, TYPEWRITER_CONFIG.deleteSpeed);
    } else {
      // Typing characters
      typewriterState.currentChar++;
      label.textContent = prefix + phrase.substring(0, typewriterState.currentChar);

      if (typewriterState.currentChar === phrase.length) {
        // Done typing, pause then start deleting
        typewriterState.isDeleting = true;
        typewriterState.timeoutId = setTimeout(typewrite, TYPEWRITER_CONFIG.pauseAtEnd);
        return;
      }

      typewriterState.timeoutId = setTimeout(typewrite, TYPEWRITER_CONFIG.typeSpeed);
    }
  }

  /**
   * Start the typewriter animation
   */
  function startTypewriter() {
    var input = document.getElementById('mealmems-restaurant-search');
    var label = document.getElementById('mealmems-typewriter-label');
    if (!input || !label) return;

    // Set initial label text
    label.textContent = TYPEWRITER_CONFIG.prefix;

    // Start typing after a short delay
    typewriterState.timeoutId = setTimeout(typewrite, 500);

    // Pause animation on focus (CSS handles hiding the label)
    input.addEventListener('focus', function() {
      typewriterState.isPaused = true;
      if (typewriterState.timeoutId) {
        clearTimeout(typewriterState.timeoutId);
      }
    });

    // Resume animation on blur if input is empty
    input.addEventListener('blur', function() {
      if (!input.value) {
        typewriterState.isPaused = false;
        typewrite();
      }
    });

    log('Typewriter animation started');
  }

  /**
   * Show loading state
   */
  function showLoading() {
    var loadingEl = document.getElementById('mealmems-search-loading');
    var errorEl = document.getElementById('mealmems-search-error');
    if (loadingEl) loadingEl.style.display = 'flex';
    if (errorEl) errorEl.style.display = 'none';
  }

  /**
   * Hide loading state
   */
  function hideLoading() {
    var loadingEl = document.getElementById('mealmems-search-loading');
    if (loadingEl) loadingEl.style.display = 'none';
  }

  /**
   * Show error message
   */
  function showError(message) {
    hideLoading();
    var errorEl = document.getElementById('mealmems-search-error');
    if (errorEl) {
      var span = errorEl.querySelector('span');
      if (span) span.textContent = message || 'Something went wrong. Please try again.';
      errorEl.style.display = 'flex';
    }
  }

  /**
   * Handle place selection from Google Places Autocomplete
   */
  async function handlePlaceSelection() {
    if (isProcessing) {
      log('Already processing, ignoring duplicate event');
      return;
    }

    var place = mealmemsAutocomplete.getPlace();
    log('Place selected:', place);

    if (!place || !place.place_id) {
      log('No place or place_id found');
      return;
    }

    isProcessing = true;
    showLoading();

    try {
      // Check if we have this restaurant in our database
      var checkUrl = MEALMEMS_CONFIG.apiBaseUrl + MEALMEMS_CONFIG.checkEndpoint +
        '?googlePlaceId=' + encodeURIComponent(place.place_id);

      log('Checking place:', checkUrl);

      var response = await fetch(checkUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to check place: ' + response.status);
      }

      var data = await response.json();
      log('Check response:', data);

      if (data.exists && data.shopifyProductUrl) {
        // Restaurant exists - redirect to product page
        log('Redirecting to existing product:', data.shopifyProductUrl);
        window.location.href = data.shopifyProductUrl;
      } else {
        // New restaurant - redirect to Next.js app for image selection
        var params = new URLSearchParams();
        params.set('name', place.name || '');
        params.set('googlePlaceId', place.place_id);

        if (place.formatted_address) {
          params.set('address', place.formatted_address);
        }

        if (place.geometry && place.geometry.location) {
          var lat = typeof place.geometry.location.lat === 'function'
            ? place.geometry.location.lat()
            : place.geometry.location.lat;
          var lng = typeof place.geometry.location.lng === 'function'
            ? place.geometry.location.lng()
            : place.geometry.location.lng;

          if (lat) params.set('lat', String(lat));
          if (lng) params.set('lng', String(lng));
        }

        var redirectUrl = MEALMEMS_CONFIG.apiBaseUrl + '/searching?' + params.toString();
        log('Redirecting to image selection:', redirectUrl);
        window.location.href = redirectUrl;
      }

    } catch (error) {
      console.error('[MealMems Search] Error:', error);
      showError('Unable to search. Please try again.');
      isProcessing = false;
    }
  }

  /**
   * Initialize Google Places Autocomplete
   * Called by the Google Maps API callback
   */
  function initMealmemsSearch() {
    var input = document.getElementById('mealmems-restaurant-search');

    if (!input) {
      log('Search input not found');
      return;
    }

    // Check if Google Maps API is loaded
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      console.error('[MealMems Search] Google Maps Places API not loaded');
      return;
    }

    log('Initializing autocomplete');

    // Initialize autocomplete
    mealmemsAutocomplete = new google.maps.places.Autocomplete(input, {
      types: ['establishment'],
      fields: ['place_id', 'name', 'formatted_address', 'geometry']
    });

    // Handle place selection
    mealmemsAutocomplete.addListener('place_changed', handlePlaceSelection);

    // Prevent form submission on enter (let autocomplete handle it)
    input.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });

    // Start typewriter animation
    startTypewriter();

    log('Autocomplete initialized');
  }

  // Expose init function globally for Google Maps callback
  window.initMealmemsSearch = initMealmemsSearch;

  // Also try to init if Google Maps is already loaded
  if (typeof google !== 'undefined' && google.maps && google.maps.places) {
    log('Google Maps already loaded, initializing...');
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initMealmemsSearch);
    } else {
      initMealmemsSearch();
    }
  }

})();
